<template>
	<div class="basket-product-group" :data-open="open">
		<h3 class="header" @click.prevent="toggleOpen">
			<span class="title" :data-basket-product-group-title="basketBlock.title">
				{{ basketBlock.title }}
				<span v-if="basketBlock.products.length > 1" v-text="`(${basketBlock.products.length})`"></span>
			</span>

			<action-badge :hide="open" v-if="groupHasDiscountProducts">Actie</action-badge>
			<div class="price">{{ formattedGroupPrice }}</div>

			<button @click.stop="toggleOpen">
				<icon-chevron-down :size="10" v-if="open" />
				<icon-chevron-up :size="10" v-else />
			</button>
		</h3>

		<ul v-show="open" role="list">
			<li v-for="product in basketBlock.products" :key="product.internalKey">
				<basket-product-line
						:show-discount-period-label="open"
						:product="product" />
			</li>
		</ul>
	</div>
</template>

<script setup lang="ts">
import BasketProductLine from '@/components/basket/BasketProductLine.vue';
import type {BasketBlock} from '@/types/basket';
import ActionBadge from '@/components/basket/ActionBadge.vue';
import {computed, ref} from 'vue';
import {getFormattedPrice} from '@/utils/priceFormatter';
import IconChevronDown from '@/components/icons/IconChevronDown.vue';
import IconChevronUp from '@/components/icons/IconChevronUp.vue';

const props = defineProps<{
		basketBlock: BasketBlock
	}>();

	const open = ref(props.basketBlock.defaultOpen ?? false);

	const formattedGroupPrice = computed(() => getFormattedPrice(props.basketBlock.totalPrice));

	const groupHasDiscountProducts = computed(() => props.basketBlock.products.some(product => product.discount != null));

	const toggleOpen = () => open.value = !open.value;
</script>

<style scoped lang="scss">
	.basket-product-group {
		color: #343434;
		background: #f7f7f7;
		margin: 0 0 8px;
		--toggle-button-width: var(--padding-right, 28px);
	}

	.header {
		display: flex;
		align-content: start;
		justify-content: start;
		font: inherit;
		color: inherit;

		padding: 15px 0 13px var(--basket-product-group-padding-x);
		margin: 0;

		cursor: pointer;

		& > * {
			flex: 0 0 auto;
		}

		.title {
			flex: 0 1 auto;
			margin: 0 auto 0 0;
		}

		.action-badge {
			margin-left: 1em;
		}

		.price {
			margin-left: 1em;
		}

		button {
			background: none;
			border: none;
			width: var(--toggle-button-width);
			height: 20px;
			padding: 0;
			cursor: pointer;
		}

		[data-open="true"] & {
			font-weight: 600;
		}
	}

	ul {
		margin: 0;
		padding: 0 var(--basket-product-group-padding-x) 24px;
		list-style: none;
	}

	li {
		padding: 0 0 0 12px;
		margin: 0;
		position: relative;

		&:before {
			content: "•";
			left: 0;
			position: absolute;
			top: 2px;
		}
	}
</style>
